import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Friend, FriendRequest } from '../types';
import { createFriendRequestNotification } from './requestsService';

const COLLECTIONS = {
  USERS: 'users',
  FRIEND_REQUESTS: 'friendRequests',
  FRIENDS: 'friends',
  NOTIFICATIONS: 'notifications',
} as const;

/**
 * Firestore service for managing friends and collaborations
 */

export const friendsService = {
  /**
   * Send a friend request
   */
  sendFriendRequest: async (
    fromUserId: string,
    fromEmail: string,
    fromDisplayName: string | undefined,
    toEmail: string
  ): Promise<void> => {
    try {
      // First, find the user by email
      const usersRef = collection(db, COLLECTIONS.USERS);
      const q = query(usersRef, where('email', '==', toEmail.toLowerCase()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error('User not found. They need to sign up first.');
      }

      const toUserDoc = snapshot.docs[0];
      const toUserId = toUserDoc.id;
      const toUserData = toUserDoc.data();
      // Check if already friends
      const friendsRef = doc(db, COLLECTIONS.USERS, fromUserId, COLLECTIONS.FRIENDS, toUserId);
      const friendDoc = await getDoc(friendsRef);
      if (friendDoc.exists()) {
        throw new Error('Already friends with this user');
      }
      // Check if request already exists
      const requestsRef = collection(db, COLLECTIONS.FRIEND_REQUESTS);
      const existingRequestQuery = query(
        requestsRef,
        where('fromUserId', '==', fromUserId),
        where('toUserId', '==', toUserId),
        where('status', '==', 'pending')
      );
      const existingRequests = await getDocs(existingRequestQuery);
      if (!existingRequests.empty) {
        throw new Error('Friend request already sent');
      }
      // Create friend request
      const requestId = `${fromUserId}_${toUserId}_${Date.now()}`;
      const requestRef = doc(db, COLLECTIONS.FRIEND_REQUESTS, requestId);
      
      const requestData: any = {
        id: requestId,
        fromUserId,
        fromEmail,
        toUserId,
        toEmail: toUserData.email || toEmail,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      
      // Only add fromDisplayName if it's not undefined
      if (fromDisplayName) {
        requestData.fromDisplayName = fromDisplayName;
      }
      
      await setDoc(requestRef, requestData);

      // Create notification for the recipient and save to Firestore
      const notification = createFriendRequestNotification(
        fromUserId,
        fromEmail,
        fromDisplayName,
        toUserId,
        requestId
      );

      // Save notification to Firestore
      const notificationRef = doc(
        db,
        COLLECTIONS.USERS,
        toUserId,
        COLLECTIONS.NOTIFICATIONS,
        notification.id
      );
      await setDoc(notificationRef, notification);
    } catch (error: any) {
      console.error('Error sending friend request:', error);
      // Re-throw with more context
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please ensure Firestore security rules are deployed.');
      }
      throw error;
    }
  },

  /**
   * Get received friend requests for a user
   */
  getReceivedRequests: async (userId: string): Promise<FriendRequest[]> => {
    const requestsRef = collection(db, COLLECTIONS.FRIEND_REQUESTS);
    const q = query(
      requestsRef,
      where('toUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as FriendRequest);
  },

  /**
   * Get sent friend requests for a user
   */
  getSentRequests: async (userId: string): Promise<FriendRequest[]> => {
    const requestsRef = collection(db, COLLECTIONS.FRIEND_REQUESTS);
    const q = query(
      requestsRef,
      where('fromUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as FriendRequest);
  },

  /**
   * Accept a friend request
   */
  acceptFriendRequest: async (requestId: string, _userId: string): Promise<void> => {
    const requestRef = doc(db, COLLECTIONS.FRIEND_REQUESTS, requestId);
    const requestDoc = await getDoc(requestRef);

    if (!requestDoc.exists()) {
      throw new Error('Friend request not found');
    }

    const request = requestDoc.data() as FriendRequest;

    // Add friend relationship for both users
    const friend1Ref = doc(db, COLLECTIONS.USERS, request.fromUserId, COLLECTIONS.FRIENDS, request.toUserId);
    const friend2Ref = doc(db, COLLECTIONS.USERS, request.toUserId, COLLECTIONS.FRIENDS, request.fromUserId);

    const now = new Date().toISOString();

    // Get user data for both users
    const fromUserRef = doc(db, COLLECTIONS.USERS, request.fromUserId);
    const toUserRef = doc(db, COLLECTIONS.USERS, request.toUserId);
    const [fromUserDoc, toUserDoc] = await Promise.all([
      getDoc(fromUserRef),
      getDoc(toUserRef),
    ]);

    const fromUserData = fromUserDoc.data();
    const toUserData = toUserDoc.data();

    await Promise.all([
      setDoc(friend1Ref, {
        id: request.toUserId,
        email: request.toEmail,
        displayName: toUserData?.displayName,
        photoURL: toUserData?.photoURL,
        addedAt: now,
      }),
      setDoc(friend2Ref, {
        id: request.fromUserId,
        email: request.fromEmail,
        displayName: fromUserData?.displayName || request.fromDisplayName,
        photoURL: fromUserData?.photoURL,
        addedAt: now,
      }),
      updateDoc(requestRef, {
        status: 'accepted',
        respondedAt: now,
      }),
    ]);
  },

  /**
   * Reject a friend request
   */
  rejectFriendRequest: async (requestId: string): Promise<void> => {
    const requestRef = doc(db, COLLECTIONS.FRIEND_REQUESTS, requestId);
    await updateDoc(requestRef, {
      status: 'rejected',
      respondedAt: new Date().toISOString(),
    });
  },

  /**
   * Cancel a sent friend request
   */
  cancelFriendRequest: async (requestId: string): Promise<void> => {
    const requestRef = doc(db, COLLECTIONS.FRIEND_REQUESTS, requestId);
    await deleteDoc(requestRef);
  },

  /**
   * Get all friends for a user
   */
  getFriends: async (userId: string): Promise<Friend[]> => {
    const friendsRef = collection(db, COLLECTIONS.USERS, userId, COLLECTIONS.FRIENDS);
    const snapshot = await getDocs(friendsRef);
    return snapshot.docs.map((doc) => doc.data() as Friend);
  },

  /**
   * Remove a friend
   */
  removeFriend: async (userId: string, friendId: string): Promise<void> => {
    const friend1Ref = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.FRIENDS, friendId);
    const friend2Ref = doc(db, COLLECTIONS.USERS, friendId, COLLECTIONS.FRIENDS, userId);
    await Promise.all([deleteDoc(friend1Ref), deleteDoc(friend2Ref)]);
  },

  /**
   * Ensure user document exists (called on login)
   */
  ensureUserDocument: async (
    userId: string,
    email: string,
    displayName?: string,
    photoURL?: string
  ): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: email.toLowerCase(),
          displayName,
          photoURL,
          createdAt: new Date().toISOString(),
        });
      } else {
        // Update user data if changed
        await updateDoc(userRef, {
          email: email.toLowerCase(),
          displayName,
          photoURL,
        });
      }
    } catch (error: any) {
      console.error('Error ensuring user document:', error);
      if (error.code === 'permission-denied') {
        console.error('Permission denied. Firestore security rules may not be deployed correctly.');
      }
      // Don't throw - allow user to continue even if this fails
    }
  },
};
